/**
 * FTP/SFTP Credential Form
 *
 * A form for configuring FTP/SFTP credentials with section-based rendering:
 * - Server: host, port, protocol
 * - Auth: authentication method, username, password
 * - Security: SSL, SSH settings, private key
 * - Options: timeout, passive mode
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

interface FTPCredentialFormProps {
  value: any;
  onChange: (value: any) => void;
  errors?: Record<string, string>;
  /** Which section to render - 'server', 'auth', 'security', 'options', or 'all' (default) */
  section?: 'server' | 'auth' | 'security' | 'options' | 'all';
}

export const FTPCredentialForm: React.FC<FTPCredentialFormProps> = ({
  value,
  onChange,
  errors = {},
  section = 'all',
}) => {
  const handleChange = (field: string, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue
    });
  };

  const protocol = value.protocol || 'ftp';
  const authType = value.authType || 'credentials';
  const sshMode = value.sshMode || 'password';
  const enableSsh = value.enableSsh ?? (protocol === 'sftp');

  // Auto-update port when protocol changes
  const handleProtocolChange = (newProtocol: string) => {
    const newPort = newProtocol === 'sftp' ? 22 : 21;
    onChange({
      ...value,
      protocol: newProtocol,
      port: value.port === 21 || value.port === 22 || !value.port ? newPort : value.port,
      enableSsh: newProtocol === 'sftp'
    });
  };

  // Server section
  const serverContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="protocol">Protocol</Label>
        <Select
          value={protocol}
          onValueChange={handleProtocolChange}
        >
          <SelectTrigger id="protocol">
            <SelectValue placeholder="Select protocol" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ftp">FTP</SelectItem>
            <SelectItem value="sftp">SFTP</SelectItem>
          </SelectContent>
        </Select>
        {errors?.protocol && (
          <p className="text-sm text-red-500">{errors.protocol}</p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="host">Host <span className="text-red-500">*</span></Label>
          <Input
            id="host"
            type="text"
            value={value.host || ''}
            onChange={(e) => handleChange('host', e.target.value)}
            placeholder="ftp.example.com"
            className="font-mono text-sm"
          />
          {errors?.host && (
            <p className="text-sm text-red-500">{errors.host}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="port">Port</Label>
          <Input
            id="port"
            type="number"
            value={value.port || (protocol === 'sftp' ? 22 : 21)}
            onChange={(e) => handleChange('port', parseInt(e.target.value))}
            placeholder={protocol === 'sftp' ? '22' : '21'}
            className="font-mono text-sm"
          />
          {errors?.port && (
            <p className="text-sm text-red-500">{errors.port}</p>
          )}
        </div>
      </div>
    </div>
  );

  // Auth section
  const authContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="authType">Authentication Type</Label>
        <Select
          value={authType}
          onValueChange={(val) => handleChange('authType', val)}
        >
          <SelectTrigger id="authType">
            <SelectValue placeholder="Select authentication type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="anonymous">Anonymous</SelectItem>
            <SelectItem value="credentials">User Credentials</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {authType === 'credentials' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username <span className="text-red-500">*</span></Label>
            <Input
              id="username"
              type="text"
              value={value.username || ''}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="username"
              className="font-mono text-sm"
            />
            {errors?.username && (
              <p className="text-sm text-red-500">{errors.username}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={value.password || ''}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Enter password"
              className="font-mono text-sm"
            />
            {errors?.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Security section
  const securityContent = (
    <div className="space-y-4">
      {protocol === 'ftp' && (
        <div className="space-y-2">
          <Label htmlFor="ssl">SSL Mode</Label>
          <Select
            value={value.ssl || 'none'}
            onValueChange={(val) => handleChange('ssl', val)}
          >
            <SelectTrigger id="ssl">
              <SelectValue placeholder="Select SSL mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="implicit">Implicit SSL</SelectItem>
              <SelectItem value="explicit">Explicit SSL</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="enableSsh">Enable SSH</Label>
          <p className="text-xs text-muted-foreground">Use SSH for secure transfer</p>
        </div>
        <Switch
          id="enableSsh"
          checked={enableSsh}
          onCheckedChange={(checked) => handleChange('enableSsh', checked)}
        />
      </div>

      {enableSsh && (
        <>
          <div className="space-y-2">
            <Label htmlFor="sshMode">SSH Authentication</Label>
            <Select
              value={sshMode}
              onValueChange={(val) => handleChange('sshMode', val)}
            >
              <SelectTrigger id="sshMode">
                <SelectValue placeholder="Select SSH mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">Password</SelectItem>
                <SelectItem value="privateKey">Private Key</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {sshMode === 'privateKey' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="privateKey">Private Key</Label>
                <Textarea
                  id="privateKey"
                  value={value.privateKey || ''}
                  onChange={(e) => handleChange('privateKey', e.target.value)}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  rows={4}
                  className="font-mono text-xs"
                />
                {errors?.privateKey && (
                  <p className="text-sm text-red-500">{errors.privateKey}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="passphrase">Key Passphrase</Label>
                <Input
                  id="passphrase"
                  type="password"
                  value={value.passphrase || ''}
                  onChange={(e) => handleChange('passphrase', e.target.value)}
                  placeholder="If key is encrypted"
                  className="font-mono text-sm"
                />
                {errors?.passphrase && (
                  <p className="text-sm text-red-500">{errors.passphrase}</p>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  // Options section
  const optionsContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="timeout">Timeout (seconds)</Label>
        <Input
          id="timeout"
          type="number"
          value={value.timeout ? Math.round(value.timeout / 1000) : 60}
          onChange={(e) => handleChange('timeout', parseInt(e.target.value) * 1000)}
          placeholder="60"
          className="font-mono text-sm"
        />
        {errors?.timeout && (
          <p className="text-sm text-red-500">{errors.timeout}</p>
        )}
      </div>

      {protocol === 'ftp' && (
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="passive">Passive Mode</Label>
            <p className="text-xs text-muted-foreground">Use passive mode for data connections</p>
          </div>
          <Switch
            id="passive"
            checked={value.passive ?? true}
            onCheckedChange={(checked) => handleChange('passive', checked)}
          />
        </div>
      )}
    </div>
  );

  // Return section-specific content or all
  if (section === 'server') return serverContent;
  if (section === 'auth') return authContent;
  if (section === 'security') return securityContent;
  if (section === 'options') return optionsContent;

  // Default: return all content (for backwards compatibility)
  return (
    <div className="space-y-6">
      {serverContent}
      <div className="border-t pt-4">{authContent}</div>
      <div className="border-t pt-4">{securityContent}</div>
      <div className="border-t pt-4">{optionsContent}</div>
    </div>
  );
};
